/**
 * `Billing` — merchant billing surfaces (ADR-0155). Rendered inside the
 * account tabbed view: `BillingTab` (your plan + change-plan picker) and
 * `InvoicesSection` (its own Invoices tab).
 *
 * Design pass (ux + product): ONE `YourPlanCard` state machine (active /
 * trialing / past_due / canceling / free) — never two overlapping cards; the
 * picker excludes the free tier and shows each paid plan's metered LIMITS;
 * the pane uses the sibling-panel header (no numbered "01 ·" kicker) and no
 * inner max-width wrapper so it fills the tab like Profile/Team/Security.
 */

import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  downgradeToFree,
  listBillingPlans,
  listInvoices,
  startCheckout,
  type BillingInvoice,
  type BillingPlan,
  type BillingPlanLimits,
  type CurrentSubscription,
} from '../../api/billing.js';
import { useC } from '../../lib/theme.js';
import { Card, Pill } from '../../components/atoms/index.js';
import { PlanAndUsageSection } from './PlanAndUsageSection.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

const ENTITLING = ['active', 'trialing', 'past_due'];

type PlansState =
  | { kind: 'loading' }
  | { kind: 'ready'; plans: readonly BillingPlan[]; current: CurrentSubscription | null }
  | { kind: 'error' };

/** Consistent account-tab section header (mono kicker + serif title + sub). */
function PanelHeader({ kicker, title, sub }: { kicker: string; title: string; sub?: string }): JSX.Element {
  const C = useC();
  return (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, textTransform: 'uppercase' }}>
        {kicker}
      </div>
      <h2 style={{ fontFamily: serifFont, fontSize: 22, letterSpacing: -0.5, color: C.text, margin: '4px 0 0' }}>
        {title}
      </h2>
      {sub !== undefined ? (
        <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5, marginTop: 4 }}>{sub}</p>
      ) : null}
    </div>
  );
}

function formatPrice(amount_minor: number, currency: string, interval: 'monthly' | 'yearly'): string {
  const major = amount_minor / 100;
  const whole = amount_minor % 100 === 0;
  const num = whole ? String(major) : major.toFixed(2);
  const symbol = currency.toLowerCase() === 'usd' ? '$' : '';
  const amount = symbol.length > 0 ? `${symbol}${num}` : `${num} ${currency.toUpperCase()}`;
  return `${amount} / ${interval === 'monthly' ? 'mo' : 'yr'}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

/** A metered-limit value → scannable label (null = unlimited; 0 = semantic). */
function formatLimit(dimension: keyof BillingPlanLimits, v: number | null): string {
  if (v === null) return 'Unlimited';
  if (v === 0) {
    if (dimension === 'transactions_monthly') return 'Observe only';
    if (dimension === 'red_team_runs_monthly') return '—';
  }
  if (dimension === 'retention_days') return `${v.toLocaleString()} days`;
  return v.toLocaleString();
}

export function Billing(): JSX.Element {
  return (
    <>
      <BillingTab />
      <InvoicesSection />
    </>
  );
}

export function BillingTab(): JSX.Element {
  const http = useOptionalHttp();
  const [state, setState] = useState<PlansState>({ kind: 'loading' });

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void listBillingPlans(http).then((res) => {
      if (cancelled) return;
      setState(
        res.kind === 'ok'
          ? { kind: 'ready', plans: res.plans, current: res.current }
          : { kind: 'error' },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [http]);

  const current = state.kind === 'ready' ? state.current : null;

  return (
    <section id="billing" className="space-y-6">
      <PanelHeader kicker="Billing" title="Your plan" sub="Your plan, usage limits, and billing history." />
      <YourPlanCard current={current} />
      {/* ADR-0263 S3 — the Plan and usage section stays hidden while
          every site is `plan_source: 'none'` (today's default). It
          sits above the picker so the plan the merchant pays for and
          the per-site readings sit together. */}
      <PlanAndUsageSection />
      <PlanPicker state={state} />
    </section>
  );
}

type DisplayState = 'active' | 'trialing' | 'past_due' | 'canceling' | 'free';

function resolveDisplayState(current: CurrentSubscription | null): DisplayState {
  if (current === null) return 'free';
  const s = current.status;
  const end = current.current_period_end !== null ? Date.parse(current.current_period_end) : null;
  const inPeriod = end !== null && end > Date.now();
  // Scheduled cancel (cancel_at_period_end): Stripe keeps the sub ACTIVE with
  // a canceled_at set — the account keeps paid access + caps until period end.
  if ((s === 'active' || s === 'trialing') && current.canceled_at !== null && inPeriod) return 'canceling';
  if (s === 'active') return 'active';
  if (s === 'trialing') return 'trialing';
  if (s === 'past_due') return 'past_due';
  // canceled / closed: still in the paid period → "canceling"; period over → free.
  if (s === 'canceled' || s === 'closed' || s === 'suspended') {
    if (inPeriod) return 'canceling';
    return 'free';
  }
  return 'free';
}

/** Single card covering every subscription state — never two overlapping cards. */
function YourPlanCard({ current }: { current: CurrentSubscription | null }): JSX.Element {
  const C = useC();
  const state = resolveDisplayState(current);

  if (state === 'free') {
    return (
      <Card className="p-6">
        <PlanCardHead name="Discovery" meta="$0 / month" tone="lime" pill="ACTIVE" check />
        <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6, margin: '16px 0' }}>
          Identity, observation, and audit export. No transactional capability — upgrade to unlock signed
          mandates and payment authorization.
        </p>
        <ul className="space-y-2" style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
          <li><span style={{ color: C.dim }}>—</span> Embed script, agent card endpoint, identity registry.</li>
          <li><span style={{ color: C.dim }}>—</span> Signed audit envelope per observation, exportable.</li>
          <li><span style={{ color: C.dim }}>—</span> Free, unlimited installs.</li>
        </ul>
      </Card>
    );
  }

  // Paid states — current is non-null here.
  const c = current as CurrentSubscription;
  const priceLabel =
    c.amount_minor !== null && c.interval !== null
      ? formatPrice(c.amount_minor, c.currency, c.interval === 'yearly' ? 'yearly' : 'monthly')
      : null;
  const end = c.current_period_end;

  let meta: string;
  let metaColor = C.muted;
  let pill = 'ACTIVE';
  let tone: 'lime' | 'sky' | 'amber' | 'coral' = 'lime';
  let check = true;
  if (state === 'active') {
    meta = end !== null ? `Renews ${formatDate(end)}` : 'Active';
  } else if (state === 'trialing') {
    pill = 'TRIAL';
    tone = 'sky';
    check = false;
    meta = end !== null ? `Trial ends ${formatDate(end)}` : 'On trial';
  } else if (state === 'past_due') {
    pill = 'PAST DUE';
    tone = 'coral';
    check = false;
    metaColor = C.coralFg;
    meta = 'Payment overdue — update your card to keep this plan.';
  } else {
    // canceling
    pill = 'CANCELED';
    tone = 'amber';
    check = false;
    meta =
      end !== null
        ? `Canceled — access until ${formatDate(end)}, then reverts to Discovery.`
        : 'Canceled — reverts to Discovery.';
  }

  return (
    <Card className="p-6">
      <PlanCardHead name={c.plan_name} meta={meta} metaColor={metaColor} price={priceLabel} tone={tone} pill={pill} check={check} />
    </Card>
  );
}

function PlanCardHead({
  name,
  meta,
  metaColor,
  price,
  tone,
  pill,
  check,
}: {
  name: string;
  meta: string;
  metaColor?: string;
  price?: string | null;
  tone: 'lime' | 'sky' | 'amber' | 'coral';
  pill: string;
  check?: boolean;
}): JSX.Element {
  const C = useC();
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
          CURRENT PLAN
        </div>
        <div style={{ fontFamily: serifFont, fontSize: 32, color: C.text, letterSpacing: -1, lineHeight: 1 }}>
          {name}
        </div>
        {price !== undefined && price !== null ? (
          <div style={{ fontFamily: monoFont, fontSize: 14, color: C.muted, marginTop: 6 }}>{price}</div>
        ) : null}
        <div style={{ fontFamily: monoFont, fontSize: 13, color: metaColor ?? C.muted, marginTop: 6, letterSpacing: 0.3 }}>
          {meta}
        </div>
      </div>
      {check === true ? (
        <Pill tone={tone} icon={Check}>{pill}</Pill>
      ) : (
        <Pill tone={tone}>{pill}</Pill>
      )}
    </div>
  );
}

type CheckoutReturn = 'success' | 'cancel' | null;

function readCheckoutReturn(): CheckoutReturn {
  if (typeof window === 'undefined') return null;
  try {
    const v = new URL(window.location.href).searchParams.get('checkout');
    if (v === 'success' || v === 'cancel') {
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      window.history.replaceState(null, '', url.toString());
      return v;
    }
  } catch {
    /* enhancement only */
  }
  return null;
}

const LIMIT_CELLS: readonly { key: keyof BillingPlanLimits; label: string }[] = [
  { key: 'sites', label: 'Sites' },
  { key: 'transactions_monthly', label: 'Transactions / mo' },
  { key: 'agent_traffic_monthly', label: 'Agent traffic' },
  { key: 'red_team_runs_monthly', label: 'Red-team / mo' },
  { key: 'retention_days', label: 'History' },
];

/** Change-plan picker — excludes the free tier, shows each paid plan's limits. */
function PlanPicker({ state }: { state: PlansState }): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const [interval, setPlanInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [banner] = useState<CheckoutReturn>(() => readCheckoutReturn());
  const [dgState, setDgState] = useState<'idle' | 'confirming' | 'busy' | 'done'>('idle');
  const [dgMessage, setDgMessage] = useState<string | null>(null);

  async function onDowngrade(): Promise<void> {
    if (http === null) return;
    setDgState('busy');
    setDgMessage(null);
    const res = await downgradeToFree(http);
    if (res.kind === 'ok') {
      setDgState('done');
      return;
    }
    setDgState('idle');
    setDgMessage(
      res.kind === 'forbidden'
        ? 'Only an owner or admin can change the plan.'
        : res.kind === 'no_subscription'
          ? 'No active subscription to downgrade.'
          : res.kind === 'rate_limited'
            ? 'Too many attempts — please wait a moment and try again.'
            : 'Could not downgrade. Please try again.',
    );
  }

  async function onSubscribe(plan_id: string): Promise<void> {
    if (http === null) return;
    setBusy(`${plan_id}:${interval}`);
    setMessage(null);
    const res = await startCheckout(http, plan_id, interval);
    if (res.kind === 'ok') {
      window.location.href = res.url;
      return;
    }
    setBusy(null);
    setMessage(
      res.kind === 'forbidden'
        ? 'Only an owner or admin can change the plan.'
        : res.kind === 'not_purchasable'
          ? 'That plan is not available for checkout right now.'
          : res.kind === 'rate_limited'
            ? 'Too many attempts — please wait a moment and try again.'
            : 'Could not start checkout. Please try again.',
    );
  }

  const allPlans = state.kind === 'ready' ? state.plans : [];
  const current = state.kind === 'ready' ? state.current : null;
  const hasActive = current !== null && ENTITLING.includes(current.status);
  // Exclude the free tier from the picker — it's represented by "Your plan".
  const plans = allPlans.filter((p) => p.slug !== 'discovery' && !p.prices.every((pr) => pr.amount_minor === 0));

  if (plans.length === 0 && banner === null) return null;

  return (
    <div className="space-y-4">
      {banner !== null ? (
        <Card className="p-4" style={{ borderColor: banner === 'success' ? C.limeFg : C.border }}>
          <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text }}>
            {banner === 'success'
              ? 'Payment received — your plan will activate in a moment.'
              : 'Checkout canceled. No changes were made.'}
          </div>
        </Card>
      ) : null}

      {plans.length > 0 ? (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
              {hasActive ? 'CHANGE PLAN' : 'PLANS'}
            </div>
            <div className="flex items-center gap-1" role="group" aria-label="Billing interval">
              {(['monthly', 'yearly'] as const).map((iv) => {
                const active = interval === iv;
                return (
                  <button
                    key={iv}
                    type="button"
                    onClick={() => setPlanInterval(iv)}
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      letterSpacing: 1,
                      padding: '4px 10px',
                      borderRadius: 2,
                      color: active ? C.ink : C.muted,
                      background: active ? C.lime : 'transparent',
                      border: `1px solid ${active ? C.lime : C.border}`,
                      cursor: 'pointer',
                    }}
                  >
                    {iv.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          {message !== null ? (
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{message}</div>
          ) : null}

          <div className="space-y-3">
            {plans.map((plan) => {
              const price = plan.prices.find((p) => p.interval === interval);
              const purchasable = price?.purchasable === true;
              const key = `${plan.plan_id}:${interval}`;
              const isCurrent = hasActive && current !== null && current.plan_id === plan.plan_id;
              return (
                <Card
                  key={plan.plan_id}
                  className="p-5"
                  {...(isCurrent ? { style: { borderColor: C.limeFg } } : {})}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text, letterSpacing: -0.5 }}>
                        {plan.name}
                      </div>
                      {plan.description !== null ? (
                        <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
                          {plan.description}
                        </p>
                      ) : null}
                      {plan.trial_days > 0 && !isCurrent ? (
                        <div style={{ marginTop: 8 }}>
                          <Pill tone="sky">{plan.trial_days}-DAY TRIAL</Pill>
                        </div>
                      ) : null}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div style={{ fontFamily: monoFont, fontSize: 15, color: C.text, marginBottom: 8 }}>
                        {price !== undefined ? formatPrice(price.amount_minor, price.currency, interval) : '—'}
                      </div>
                      {isCurrent ? (
                        <Pill tone="lime" icon={Check}>CURRENT PLAN</Pill>
                      ) : (
                        <button
                          type="button"
                          disabled={!purchasable || busy === key}
                          onClick={() => void onSubscribe(plan.plan_id)}
                          style={{
                            fontFamily: sansFont,
                            fontSize: 13,
                            fontWeight: 500,
                            padding: '8px 16px',
                            borderRadius: 2,
                            color: purchasable ? C.ink : C.muted,
                            background: purchasable ? C.lime : 'transparent',
                            border: `1px solid ${purchasable ? C.lime : C.border}`,
                            cursor: purchasable && busy !== key ? 'pointer' : 'not-allowed',
                            opacity: busy === key ? 0.6 : 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {busy === key
                            ? 'Redirecting…'
                            : purchasable
                              ? hasActive
                                ? 'Switch'
                                : 'Subscribe'
                              : 'Unavailable'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Limits strip */}
                  <div
                    className="grid grid-cols-2 sm:grid-cols-4 gap-4"
                    style={{ borderTop: `1px solid ${C.borderSoft}`, marginTop: 16, paddingTop: 14 }}
                  >
                    {LIMIT_CELLS.map((cell) => (
                      <div key={cell.key}>
                        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.dim, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                          {cell.label}
                        </div>
                        <div style={{ fontFamily: sansFont, fontSize: 15, color: C.text, fontWeight: 500, marginTop: 2 }}>
                          {formatLimit(cell.key, plan.limits[cell.key])}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>

          {hasActive && current !== null && resolveDisplayState(current) !== 'canceling' ? (
            <Card className="p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div style={{ fontFamily: serifFont, fontSize: 18, color: C.text, letterSpacing: -0.3 }}>
                    Downgrade to Discovery (Free)
                  </div>
                  <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
                    You&rsquo;ll keep {current.plan_name} until{' '}
                    {current.current_period_end !== null ? formatDate(current.current_period_end) : 'the end of your billing period'}
                    , then move to the free Discovery tier. No refund for the current period.
                  </p>
                  {dgMessage !== null ? (
                    <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, marginTop: 8 }}>{dgMessage}</div>
                  ) : null}
                </div>
                <div className="flex-shrink-0">
                  {dgState === 'done' ? (
                    <span style={{ fontFamily: sansFont, fontSize: 13, color: C.limeFg }}>
                      Downgrade scheduled ✓
                    </span>
                  ) : dgState === 'confirming' || dgState === 'busy' ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={dgState === 'busy'}
                        onClick={() => void onDowngrade()}
                        style={{
                          fontFamily: sansFont,
                          fontSize: 13,
                          fontWeight: 500,
                          padding: '8px 14px',
                          borderRadius: 2,
                          color: C.coralFg,
                          background: 'transparent',
                          border: `1px solid ${C.coralFg}`,
                          cursor: dgState === 'busy' ? 'not-allowed' : 'pointer',
                          opacity: dgState === 'busy' ? 0.6 : 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {dgState === 'busy' ? 'Scheduling…' : 'Confirm downgrade'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDgState('idle')}
                        style={{
                          fontFamily: sansFont,
                          fontSize: 13,
                          padding: '8px 12px',
                          borderRadius: 2,
                          color: C.muted,
                          background: 'transparent',
                          border: `1px solid ${C.border}`,
                          cursor: 'pointer',
                        }}
                      >
                        Keep plan
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDgState('confirming')}
                      style={{
                        fontFamily: sansFont,
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '8px 14px',
                        borderRadius: 2,
                        color: C.muted,
                        background: 'transparent',
                        border: `1px solid ${C.border}`,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Downgrade to Free
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** The account's billing history — its own Invoices tab. Renders nothing when
 *  there are no invoices or the caller can't view them (reviewer/viewer → 403). */
export function InvoicesSection(): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const [invoices, setInvoices] = useState<BillingInvoice[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void listInvoices(http).then((res) => {
      if (cancelled) return;
      setInvoices(res.kind === 'ok' ? [...res.invoices] : []);
      setHasMore(res.kind === 'ok' ? res.has_more : false);
    });
    return () => {
      cancelled = true;
    };
  }, [http]);

  async function loadMore(): Promise<void> {
    if (http === null || invoices === null || invoices.length === 0) return;
    setLoadingMore(true);
    const res = await listInvoices(http, invoices[invoices.length - 1]!.id);
    if (res.kind === 'ok') {
      setInvoices((prev) => [...(prev ?? []), ...res.invoices]);
      setHasMore(res.has_more);
    }
    setLoadingMore(false);
  }

  if (invoices === null || invoices.length === 0) return null;

  return (
    <section className="space-y-4">
      <PanelHeader kicker="Invoices" title="Billing history" />
      <Card style={{ overflow: 'hidden', padding: 0 }}>
        {invoices.map((inv, idx) => (
          <div
            key={inv.id}
            className="flex items-center justify-between gap-3 px-5 py-4 flex-wrap"
            style={{ borderTop: idx > 0 ? `1px solid ${C.borderSoft}` : 'none' }}
          >
            <div className="min-w-0">
              <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text }}>{formatDate(inv.created)}</div>
              <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginTop: 2 }}>
                {(inv.number ?? inv.id)} · {inv.status}
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <span style={{ fontFamily: monoFont, fontSize: 13, color: C.text }}>
                {inv.currency.toLowerCase() === 'usd' ? '$' : ''}
                {(inv.amount_paid / 100).toFixed(2)}
                {inv.currency.toLowerCase() !== 'usd' ? ` ${inv.currency.toUpperCase()}` : ''}
              </span>
              {inv.hosted_invoice_url !== null ? (
                <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" style={{ fontFamily: sansFont, fontSize: 13, color: C.limeFg, textDecoration: 'none' }}>
                  View
                </a>
              ) : null}
              {inv.pdf_url !== null ? (
                <a href={inv.pdf_url} target="_blank" rel="noreferrer" style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, textDecoration: 'none' }}>
                  PDF
                </a>
              ) : null}
            </div>
          </div>
        ))}
      </Card>
      {hasMore ? (
        <button
          type="button"
          disabled={loadingMore}
          onClick={() => void loadMore()}
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: '8px 14px',
            cursor: loadingMore ? 'not-allowed' : 'pointer',
            opacity: loadingMore ? 0.6 : 1,
          }}
        >
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      ) : null}
    </section>
  );
}

/** Free-tier plan card — retained as a named export for the (legacy) standalone
 *  billing surfaces that still import it; the tabbed account view uses
 *  `YourPlanCard`'s free state instead. */
export function BillingDiscoveryCard(): JSX.Element {
  return <YourPlanCard current={null} />;
}
